import { System } from '../core/System';
// Entity import removed - not needed in this system
import { PlayerComponent } from '../components/PlayerComponent';
import { MovementComponent } from '../components/MovementComponent';
import { TransformComponent } from '../components/TransformComponent';
import { Vector2 } from '../core/Vector2';
import { EventManager } from '../../events/EventManager';
import { Event } from '../../events/Event';
import { KeyboardEvent } from '../../events/KeyboardEvent';
import { EventListener } from '../../events/EventListener';

/**
 * Event fired when player wants to shoot
 */
export class PlayerFireEvent extends Event {
  constructor(
    public readonly playerId: string,
    public readonly position: Vector2,
    public readonly direction: Vector2
  ) {
    super('player_fire');
  }
}

/**
 * PlayerInputSystem handles player input and fires events
 * All logic for player behavior is contained in systems, not components
 */
export class PlayerInputSystem extends System implements EventListener {
  private eventManager: EventManager;
  private keysPressed: Set<string> = new Set();

  constructor() {
    super();
    this.registerRequiredComponent(PlayerComponent);
    this.registerRequiredComponent(MovementComponent);
    this.registerRequiredComponent(TransformComponent);
    this.eventManager = EventManager.getInstance();

    // Subscribe to keyboard events
    this.eventManager.subscribe('keydown', this);
    this.eventManager.subscribe('keyup', this);
  }

  public onEvent(event: Event): void {
    if (event instanceof KeyboardEvent) {
      if (event.getType() === 'keydown') {
        this.keysPressed.add(event.getKey());
      } else if (event.getType() === 'keyup') {
        this.keysPressed.delete(event.getKey());
      }
    }
  }

  private isKeyPressed(key: string): boolean {
    return this.keysPressed.has(key);
  }

  public update(deltaTime: number): void {
    const currentTime = performance.now() / 1000; // Convert to seconds

    for (const entity of this.getEntities()) {
      const player = this.componentManager.getComponent(
        entity,
        PlayerComponent
      ) as PlayerComponent;
      const movement = this.componentManager.getComponent(
        entity,
        MovementComponent
      ) as MovementComponent;
      const transform = this.componentManager.getComponent(
        entity,
        TransformComponent
      ) as TransformComponent;

      if (!player.isLocalPlayer) continue; // Only handle local player input

      // Handle movement input
      this.handleMovementInput(movement, deltaTime);

      // Handle firing input - Logic that was in Player.canFire() now belongs here
      this.handleFireInput(player, transform, currentTime);
    }
  }

  private handleMovementInput(
    movement: MovementComponent,
    deltaTime: number
  ): void {
    const acceleration = new Vector2(0, 0);
    const accelerationAmount = 600; // pixels per second squared

    // Get input directions
    if (this.isKeyPressed('w') || this.isKeyPressed('ArrowUp')) {
      acceleration.y -= accelerationAmount;
    }
    if (this.isKeyPressed('s') || this.isKeyPressed('ArrowDown')) {
      acceleration.y += accelerationAmount;
    }
    if (this.isKeyPressed('a') || this.isKeyPressed('ArrowLeft')) {
      acceleration.x -= accelerationAmount;
    }
    if (this.isKeyPressed('d') || this.isKeyPressed('ArrowRight')) {
      acceleration.x += accelerationAmount;
    }

    // Normalize diagonal movement
    if (acceleration.magnitude() > 0) {
      acceleration.normalize();
      acceleration.x *= accelerationAmount;
      acceleration.y *= accelerationAmount;
    }

    movement.acceleration = acceleration;
  }

  private handleFireInput(
    player: PlayerComponent,
    transform: TransformComponent,
    currentTime: number
  ): void {
    // Check if player is trying to fire
    if (this.isKeyPressed(' ') || this.isKeyPressed('Space')) {
      // Check if enough time has passed since last fire (logic from Player.canFire())
      if (currentTime - player.lastFireTime >= player.fireRate) {
        // Update last fire time
        player.lastFireTime = currentTime;

        // Fire projectile - emit event instead of creating projectile directly
        const fireDirection = new Vector2(0, -1); // Default up direction
        this.eventManager.publish(
          new PlayerFireEvent(
            player.playerId,
            transform.position.clone(),
            fireDirection
          )
        );
      }
    }
  }
}
